import React, { useEffect, useRef, useState, useCallback } from 'react';

// Constants for physics
const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
const MASCOT_W = isMobile ? 40 : 56;
const MASCOT_H = isMobile ? 46 : 64;
const GRAVITY = 0.65;
const FRICTION = 0.92;
const BOUNCE = 0.42;
const TERMINAL_VY = 22;
const STOP_THRESHOLD = 0.4;

// Quip pools
const IDLE = ['psst', 'hi!', '👀', 'beep boop', 'hello?', 'pick me up!', 'boring...', 'zzz...', 'wheee', 'meep'];
const DRAG = ['where we going?', 'wheee!', "I'm flying!", 'put me down!', 'careful!', 'beep!', 'so high!'];
const THROW = ['yeet!', 'INCOMING!', 'AAAAA', 'catch me!', 'wheeeeee', 'oof!'];
const LAND_FLOOR = ['ow!', 'phew', 'safe!', '*thud*', 'made it'];
const LAND_PLATFORM = ['comfy!', 'I live here now', 'nice spot', 'best perch', 'cozy!', 'aha!', 'noice'];
const WALL = ['ouch', 'wall!', 'oof', 'rude'];
const WALK = ['off we go!', 'walking', 'exploring', 'wheee', 'trotting along', 'adventuring'];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

export default function InteractiveMascot({ isDarkBackground = false }) {
  const [pos, setPos] = useState(() => ({ x: 32, y: typeof window !== 'undefined' ? window.innerHeight - 100 : 600 }));
  const [isDragging, setDragging] = useState(false);
  const [bubble, setBubble] = useState(null);
  const [mood, setMood] = useState('happy'); // happy | shocked | dizzy | blink
  const [resting, setResting] = useState(true);
  const [isWalking, setIsWalking] = useState(false);
  const velRef = useRef({ x: 0, y: 0 });
  const lastMouse = useRef({ x: 0, y: 0, t: Date.now() });
  const dragOffset = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);
  const containerRef = useRef(null);
  const bubbleTimer = useRef(null);
  const restingRef = useRef(true);
  const walkTimer = useRef(null);
  const walkDirectionRef = useRef(0);

  const showBubble = useCallback((text, dur = 1600) => {
    setBubble(text);
    if (bubbleTimer.current) clearTimeout(bubbleTimer.current);
    bubbleTimer.current = setTimeout(() => setBubble(null), dur);
  }, []);

  // Idle quips
  useEffect(() => {
    const id = setInterval(() => {
      if (!isDragging && restingRef.current && Math.random() > 0.55) {
        showBubble(pick(IDLE), 1600);
      }
    }, 5500);
    return () => clearInterval(id);
  }, [isDragging, showBubble]);

  // Blink animation
  useEffect(() => {
    const id = setInterval(() => {
      if (mood === 'happy' && !isDragging) {
        setMood('blink');
        setTimeout(() => setMood('happy'), 150);
      }
    }, 4200);
    return () => clearInterval(id);
  }, [mood, isDragging]);

  // Autonomous walking
  useEffect(() => {
    if (isDragging) return;

    // Ensure walking direction is set
    if (walkDirectionRef.current === 0) {
      const direction = Math.random() > 0.5 ? 1 : -1;
      walkDirectionRef.current = direction;
      setIsWalking(true);
      velRef.current.x = direction * 1.5;
    }

    const pendingWalkTimer = walkTimer.current;
    return () => {
      if (pendingWalkTimer) clearTimeout(pendingWalkTimer);
    };
  }, [isDragging]);

  // Drag handlers
  const onPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    setResting(false);
    restingRef.current = false;
    setMood('shocked');
    setIsWalking(false);
    if (walkTimer.current) clearTimeout(walkTimer.current);
    showBubble(pick(['hey!', 'eek!', 'oh!']), 700);
    velRef.current = { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    dragOffset.current = { x: cx - rect.left, y: cy - rect.top };
    lastMouse.current = { x: cx, y: cy, t: Date.now() };
    try { e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId); } catch (_e) {}
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const newX = cx - dragOffset.current.x;
      const newY = cy - dragOffset.current.y;
      const now = Date.now();
      const dt = Math.max(1, now - lastMouse.current.t);
      velRef.current = {
        x: ((cx - lastMouse.current.x) / dt) * 16,
        y: ((cy - lastMouse.current.y) / dt) * 16,
      };
      // Drag quip occasionally
      const speed = Math.hypot(velRef.current.x, velRef.current.y);
      if (speed > 6 && Math.random() > 0.92) {
        showBubble(pick(DRAG), 700);
      }
      lastMouse.current = { x: cx, y: cy, t: now };
      setPos({ x: newX, y: newY });
    };
    const onUp = () => {
      setDragging(false);
      setIsWalking(false);
      if (walkTimer.current) clearTimeout(walkTimer.current);
      const v = velRef.current;
      const speed = Math.hypot(v.x, v.y);
      if (speed > 9) {
        setMood('dizzy');
        showBubble(pick(THROW), 1000);
      } else {
        setMood('happy');
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [isDragging, showBubble]);

  // Physics loop
  useEffect(() => {
    if (isDragging) return;
    let lastWallTime = 0;

    const step = () => {
      setPos((p) => {
        let { x, y } = p;
        let vx = velRef.current.x;
        let vy = velRef.current.y;

        vy = Math.min(vy + GRAVITY, TERMINAL_VY);
        
        // Always apply walking velocity when not dragging
        if (!isDragging && walkDirectionRef.current !== 0) {
          vx = walkDirectionRef.current * 0.3;
        } else {
          vx *= 0.995;
        }

        let newX = x + vx;
        let newY = y + vy;

        const W = window.innerWidth;
        const H = window.innerHeight;

        // Wall bounce
        if (newX < 0) {
          newX = 0; 
          if (!isDragging) {
            walkDirectionRef.current *= -1;
            vx = walkDirectionRef.current * 1.5;
          } else {
            vx = -vx * BOUNCE;
          }
          if (Math.abs(vx) > 2 && Date.now() - lastWallTime > 400) { showBubble(pick(WALK), 700); lastWallTime = Date.now(); }
        }
        if (newX > W - MASCOT_W) {
          newX = W - MASCOT_W; 
          if (!isDragging) {
            walkDirectionRef.current *= -1;
            vx = walkDirectionRef.current * 1.5;
          } else {
            vx = -vx * BOUNCE;
          }
          if (Math.abs(vx) > 2 && Date.now() - lastWallTime > 400) { showBubble(pick(WALK), 700); lastWallTime = Date.now(); }
        }

        // Default floor
        const floorY = H - MASCOT_H - 4;

        // Platform collision (from above only)
        let landY = floorY;
        let landedOnPlatform = false;
        const platforms = document.querySelectorAll('[data-platform="true"]');
        const mascotCenterX = newX + MASCOT_W / 2;
        const wasBottom = y + MASCOT_H;
        platforms.forEach((p) => {
          const r = p.getBoundingClientRect();
          if (r.top < 0 || r.top > H) return;
          if (mascotCenterX < r.left + 6 || mascotCenterX > r.right - 6) return;
          // Was above or at platform top, now would cross it
          if (wasBottom <= r.top + 6 && newY + MASCOT_H >= r.top) {
            if (r.top - MASCOT_H < landY) {
              landY = r.top - MASCOT_H;
              landedOnPlatform = true;
            }
          }
        });

        if (newY >= landY) {
          const hitVy = Math.abs(vy);
          newY = landY;
          if (hitVy > 5) {
            vy = -vy * BOUNCE;
            if (Math.abs(vy) < 2) vy = 0;
          } else {
            vy = 0;
            vx *= FRICTION;
            if (Math.abs(vx) < STOP_THRESHOLD) vx = 0;
          }
          // Landing event (when we transitioned from falling to resting)
          if (vy === 0 && Math.abs(vx) < STOP_THRESHOLD) {
            if (!restingRef.current) {
              restingRef.current = true;
              setResting(true);
              setMood('happy');
              showBubble(pick(landedOnPlatform ? LAND_PLATFORM : LAND_FLOOR), 1400);
            }
          }
        } else {
          if (restingRef.current) {
            restingRef.current = false;
            setResting(false);
          }
        }

        velRef.current = { x: vx, y: vy };
        return { x: newX, y: newY };
      });

      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isDragging, showBubble]);

  // Reposition on window resize
  useEffect(() => {
    const onResize = () => {
      setPos((p) => ({
        x: Math.min(p.x, window.innerWidth - MASCOT_W),
        y: Math.min(p.y, window.innerHeight - MASCOT_H),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Rotation visual based on velocity
  const rotation = isDragging ? Math.max(-20, Math.min(20, velRef.current.x * 1.2)) : (mood === 'dizzy' ? 12 : 0);

  return (
    <div
      ref={containerRef}
      data-native-cursor
      className="mascot-interactive"
      onPointerDown={onPointerDown}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: MASCOT_W,
        height: MASCOT_H,
        cursor: 'pointer',
        zIndex: 9999,
        userSelect: 'none',
        touchAction: 'none',
      }}
    >
      {bubble && (
        <div
          className="absolute left-1/2 px-2 py-1 bg-cream-50 rounded text-[10px] whitespace-nowrap shadow-md border border-cream-300 pointer-events-none"
          style={{ top: -28, transform: 'translateX(-50%)', color: '#1c1c1e' }}
        >
          {bubble}
          <span
            style={{
              position: 'absolute', left: '50%', bottom: -5, transform: 'translateX(-50%) rotate(45deg)',
              width: 6, height: 6, background: '#faf8f3', borderRight: '1px solid #d4cfc1', borderBottom: '1px solid #d4cfc1',
            }}
          />
        </div>
      )}
      <svg
        width={MASCOT_W} height={MASCOT_H} viewBox="0 0 56 64"
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: isDragging ? 'transform 0.05s linear' : 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
        className={resting && !isDragging ? 'float-y' : ''}
      >
        {/* Antenna */}
        <line x1="28" y1="8" x2="28" y2="16" stroke={isDarkBackground ? '#f3f1eb' : '#2a2a2a'} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="28" cy="7" r="2" fill={isDragging ? '#c4757e' : (isDarkBackground ? '#f3f1eb' : '#2a2a2a')}>
          {isDragging && <animate attributeName="r" values="2;3;2" dur="0.4s" repeatCount="indefinite" />}
        </circle>
        {/* Body */}
        <rect x="8" y="16" width="40" height="36" rx="6" fill={isDarkBackground ? '#f3f1eb' : '#1c1c1e'} />
        {/* Eyes */}
        {mood === 'happy' && (
          <g>
            <circle cx="19" cy="32" r="3" fill={isDarkBackground ? '#1c1c1e' : '#f3f1eb'} />
            <circle cx="37" cy="32" r="3" fill={isDarkBackground ? '#1c1c1e' : '#f3f1eb'} />
            <circle cx="20" cy="31" r="0.8" fill={isDarkBackground ? '#f3f1eb' : '#1c1c1e'} />
            <circle cx="38" cy="31" r="0.8" fill={isDarkBackground ? '#f3f1eb' : '#1c1c1e'} />
          </g>
        )}
        {mood === 'blink' && (
          <g>
            <line x1="16" y1="32" x2="22" y2="32" stroke={isDarkBackground ? '#1c1c1e' : '#f3f1eb'} strokeWidth="1.6" strokeLinecap="round" />
            <line x1="34" y1="32" x2="40" y2="32" stroke={isDarkBackground ? '#1c1c1e' : '#f3f1eb'} strokeWidth="1.6" strokeLinecap="round" />
          </g>
        )}
        {mood === 'shocked' && (
          <g>
            <circle cx="19" cy="32" r="4" fill={isDarkBackground ? '#1c1c1e' : '#f3f1eb'} />
            <circle cx="37" cy="32" r="4" fill={isDarkBackground ? '#1c1c1e' : '#f3f1eb'} />
            <circle cx="19" cy="32" r="1.4" fill={isDarkBackground ? '#f3f1eb' : '#1c1c1e'} />
            <circle cx="37" cy="32" r="1.4" fill={isDarkBackground ? '#f3f1eb' : '#1c1c1e'} />
          </g>
        )}
        {mood === 'dizzy' && (
          <g>
            <path d="M16 30 L22 34 M16 34 L22 30" stroke={isDarkBackground ? '#1c1c1e' : '#f3f1eb'} strokeWidth="1.4" strokeLinecap="round" />
            <path d="M34 30 L40 34 M34 34 L40 30" stroke={isDarkBackground ? '#1c1c1e' : '#f3f1eb'} strokeWidth="1.4" strokeLinecap="round" />
          </g>
        )}
        {/* Mouth */}
        <path
          d={
            mood === 'happy' || mood === 'blink' ? 'M22 42 Q28 46 34 42' :
            mood === 'shocked' ? 'M25 44 A3 3 0 1 0 31 44 A3 3 0 1 0 25 44' :
            'M22 43 Q28 40 34 43'
          }
          stroke={isDarkBackground ? '#1c1c1e' : '#f3f1eb'} strokeWidth="1.6" strokeLinecap="round" fill={mood === 'shocked' ? (isDarkBackground ? '#f3f1eb' : '#0a0a0a') : 'none'}
        />
        {/* Legs */}
        {walkDirectionRef.current !== 0 ? (
          // Walking animation - legs swing back and forth
          <>
            <g transform="translate(17, 52) rotate(-15)">
              <rect x="-3" y="0" width="6" height="8" fill={isDarkBackground ? '#f3f1eb' : '#1c1c1e'} rx="1" />
              <animateTransform attributeName="transform" type="rotate" values="-15; 15; -15" dur="0.6s" repeatCount="indefinite" additive="sum" />
            </g>
            <g transform="translate(39, 52) rotate(15)">
              <rect x="-3" y="0" width="6" height="8" fill={isDarkBackground ? '#f3f1eb' : '#1c1c1e'} rx="1" />
              <animateTransform attributeName="transform" type="rotate" values="15; -15; 15" dur="0.6s" repeatCount="indefinite" additive="sum" />
            </g>
          </>
        ) : (
          // Idle - static legs
          <>
            <rect x="14" y="52" width="6" height="8" fill={isDarkBackground ? '#f3f1eb' : '#1c1c1e'} rx="1" />
            <rect x="36" y="52" width="6" height="8" fill={isDarkBackground ? '#f3f1eb' : '#1c1c1e'} rx="1" />
          </>
        )}
        {/* Cheeks when happy */}
        {(mood === 'happy' || mood === 'blink') && (
          <g opacity="0.6">
            <circle cx="14" cy="40" r="2" fill={isDarkBackground ? '#c4757e' : '#e8a5a8'} />
            <circle cx="42" cy="40" r="2" fill={isDarkBackground ? '#c4757e' : '#e8a5a8'} />
          </g>
        )}
      </svg>
    </div>
  );
}
